import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Check, Heart, Sparkles, X, Users, MessageCircle } from "lucide-react";
import { loginWithReturn } from "../lib/loginWithReturn.js";
import { apiClient, FeedbackCategory } from "../lib/apiClient.js";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";

interface HelpPawseumModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const feedbackCategories = [
  {
    id: "pawmatch",
    titleKey: "helpModal.categories.pawmatch.title",
    subtitleKey: "helpModal.categories.pawmatch.subtitle",
  },
  {
    id: "features",
    titleKey: "helpModal.categories.features.title",
    subtitleKey: "helpModal.categories.features.subtitle",
  },
  {
    id: "general",
    titleKey: "helpModal.categories.general.title",
    subtitleKey: "helpModal.categories.general.subtitle",
  },
  {
    id: "help",
    titleKey: "helpModal.categories.help.title",
    subtitleKey: "helpModal.categories.help.subtitle",
  },
] as const satisfies readonly { id: FeedbackCategory; titleKey: string; subtitleKey: string }[];

export const HelpPawseumModal: React.FC<HelpPawseumModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation("common");
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } =
    useAuth0();
  const [step, setStep] = useState<1 | 2>(1);
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setCategory(null);
      setMessage("");
      setSubmitting(false);
      setSubmitted(false);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!submitted) return;
    const timer = setTimeout(() => {
      onClose();
    }, 2000);
    return () => clearTimeout(timer);
  }, [submitted, onClose]);

  const handleProceedToFeedback = () => {
    if (!isAuthenticated) {
      loginWithReturn(loginWithRedirect);
      return;
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!category) {
      setError(t("helpModal.errors.chooseCategory"));
      return;
    }

    if (!message.trim()) {
      setError(t("helpModal.errors.writeMessage"));
      return;
    }

    if (!isAuthenticated) {
      setError(t("helpModal.errors.loginRequired"));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: "openid profile email",
        },
      });

      const authHeaders = {
        Authorization: `Bearer ${token}`,
      };

      await apiClient.submitFeedback(
        {
          category,
          message: message.trim(),
        },
        authHeaders,
      );

      setSubmitted(true);
      toast.success(`${t("helpModal.toastSuccess")} 💜`, {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (submitError) {
      console.error("Erro ao submeter feedback:", submitError);
      setError(t("helpModal.errors.generic"));
      toast.error(t("helpModal.toastError"), {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black bg-opacity-50 transition-opacity duration-300 animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto animate-in zoom-in-95 duration-300"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="sticky top-0 bg-gradient-to-r from-main-purple to-golden p-6 rounded-t-3xl">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  {step === 1 ? t("helpModal.step1.title") : t("helpModal.step2.title")}
                </h2>
                <p className="text-white/90 mt-1">
                  {step === 1
                    ? t("helpModal.step1.subtitle")
                    : t("helpModal.step2.subtitle")}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                aria-label={t("helpModal.closeModal")}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {submitted ? (
              <div className="flex flex-col items-center text-center gap-4 py-10">
                <div className="relative">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-main-purple/30 animate-ping">
                    <Heart className="w-10 h-10" />
                  </div>
                  <div className="absolute -top-2 -right-4 text-golden animate-bounce">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <div className="w-20 h-20 rounded-full bg-main-purple/10 flex items-center justify-center animate-in zoom-in-95 duration-500">
                    <Check className="w-10 h-10 text-main-purple" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {t("helpModal.thanksTitle")}
                </h3>
                <p className="text-gray-600">
                  {t("helpModal.thanksBody")}
                </p>
              </div>
            ) : step === 1 ? (
              <>
                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-main-purple/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-main-purple" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {t("helpModal.ways.spread.title")}
                      </h3>
                      <p className="text-gray-600">
                        {t("helpModal.ways.spread.body")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-golden/10 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-golden" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {t("helpModal.ways.shelters.title")}
                      </h3>
                      <p className="text-gray-600">
                        {t("helpModal.ways.shelters.body")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-main-blue/10 flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-main-blue" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {t("helpModal.ways.opinion.title")}
                      </h3>
                      <p className="text-gray-600">
                        {t("helpModal.ways.opinion.body")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-gray-700 text-center">
                    <Trans
                      t={t}
                      i18nKey="helpModal.everyActionCounts"
                      components={{ strong: <strong className="text-main-purple" /> }}
                    />
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-sm font-medium text-gray-500 hover:text-gray-800"
                  >
                    {t("actions.close")}
                  </button>
                  <button
                    type="button"
                    onClick={handleProceedToFeedback}
                    className="bg-main-purple text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
                  >
                    {isAuthenticated
                      ? t("helpModal.giveFeedback")
                      : t("helpModal.loginToGiveFeedback")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t("helpModal.categoryLabel")}
                  </label>
                  <div className="space-y-3">
                    {feedbackCategories.map((option) => {
                      const isSelected = option.id === category;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          className={`w-full border rounded-xl p-4 text-left transition-all duration-200 ${
                            isSelected
                              ? "border-main-purple bg-main-purple/10 shadow-sm"
                              : "border-gray-200 hover:border-main-purple/60 hover:shadow-sm"
                          }`}
                          onClick={() => setCategory(option.id)}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                {t(option.titleKey)}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {t(option.subtitleKey)}
                              </p>
                            </div>
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                isSelected
                                  ? "border-main-purple bg-main-purple"
                                  : "border-gray-300"
                              }`}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="feedback-message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t("helpModal.messageLabel")}
                  </label>
                  <textarea
                    id="feedback-message"
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    rows={6}
                    maxLength={1000}
                    className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-main-purple/50"
                    placeholder={t("helpModal.messagePlaceholder")}
                  />
                  <div className="text-xs text-gray-400 mt-2 text-right">
                    {message.length}/1000
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-red-500 mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                    {error}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-sm font-medium text-gray-500 hover:text-gray-800"
                    disabled={submitting}
                  >
                    {t("actions.back")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || !category || !message.trim()}
                    className="bg-main-purple text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:scale-105 disabled:hover:scale-100"
                  >
                    {submitting ? t("helpModal.sending") : t("helpModal.sendFeedback")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpPawseumModal;
